import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmProvider";

const toText = (v) => {
  if (v == null) return "-";
  if (typeof v === "string") return v || "-";
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(toText).filter(x => x && x !== "-").join(" → ") || "-";
  if (typeof v === "object") {
    const parts = Object.entries(v)
      .filter(([, val]) => val != null && val !== "")
      .map(([k, val]) => `${k}: ${typeof val === "object" ? JSON.stringify(val) : val}`);
    return parts.join(" · ") || "-";
  }
  return String(v);
};

export default function SwipesPage() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ source_url: "", author: "", content: "", tags: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => { const r = await api.get("/swipes"); setItems(r.data); };
  useEffect(()=>{ load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/swipes", { ...form, tags: form.tags.split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success("Swipe deconstructed, pattern added to your library.");
      setForm({ source_url:"", author:"", content:"", tags:"" });
      setShowAdd(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    const s = items.find(x => x.id === id);
    const label = s?.author || (s?.content || "").slice(0, 60) || "This swipe";
    const ok = await confirm({
      title: "Remove this swipe?",
      description: `"${label}" will be moved to Trash. You can restore it anytime.`,
      confirmText: "Move to Trash",
    });
    if (!ok) return;
    await api.delete(`/swipes/${id}`);
    toast.success("Moved to Trash. Restore anytime.");
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Swipe file"
        title="A pattern library, not a copy library."
        subtitle="Save any post you admire. She'll extract hook · structure · format · why-it-works, patterns only, never wording."
        action={<button data-testid="swipe-add-btn" className="btn btn-accent" onClick={()=>setShowAdd(true)}><Plus size={16}/> Add swipe</button>}
      />

      {items.length === 0 ? (
        <div className="card p-16 text-center">
          <div className="eyebrow mb-2">Empty</div>
          <p className="font-serif text-3xl mb-3">Save a viral post to get started.</p>
          <p className="text-sm max-w-md mx-auto" style={{color:'var(--ink-2)'}}>Paste the text of any post you'd love to write. She'll deconstruct it so you can remix the pattern in your own voice.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((s) => (
            <article key={s.id} data-testid={`swipe-item-${s.id}`} className="card p-5 fade-up">
              <div className="flex justify-between items-start mb-3 gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-mono truncate" style={{color:'var(--ink-3)'}}>{s.author || "Unknown"}</div>
                  {s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="text-xs link-underline truncate block">{s.source_url}</a>}
                </div>
                <button onClick={()=>remove(s.id)} className="p-1.5 border hairline-2 hover:border-black shrink-0"><Trash size={12}/></button>
              </div>
              <p className="text-sm mb-4 line-clamp-4 whitespace-pre-wrap" style={{color:'var(--ink-2)'}}>{s.content}</p>
              <div className="space-y-1 text-xs  pt-3">
                <div className="flex gap-2"><span className="eyebrow shrink-0 w-16">Hook</span><span>{toText(s.hook_type)}</span></div>
                <div className="flex gap-2"><span className="eyebrow shrink-0 w-16">Struct.</span><span>{toText(s.structure)}</span></div>
                <div className="flex gap-2"><span className="eyebrow shrink-0 w-16">Format</span><span>{toText(s.format)}</span></div>
                <div className="flex gap-2"><span className="eyebrow shrink-0 w-16 mt-0.5">Why</span><span style={{color:'var(--accent)'}}>{toText(s.why_it_works)}</span></div>
              </div>
            </article>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.55)'}} onClick={()=>setShowAdd(false)}>
          <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="card p-6 w-full max-w-2xl" data-testid="swipe-add-form">
            <div className="eyebrow mb-2">New swipe</div>
            <h3 className="font-serif text-3xl mb-6">Give her a pattern to learn.</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <label className="block">
                <div className="eyebrow mb-2">Author (optional)</div>
                <input data-testid="swipe-form-author" className="input" value={form.author} onChange={e=>setForm({...form, author:e.target.value})} />
              </label>
              <label className="block">
                <div className="eyebrow mb-2">Source URL (optional)</div>
                <input data-testid="swipe-form-url" className="input" value={form.source_url} onChange={e=>setForm({...form, source_url:e.target.value})} />
              </label>
            </div>
            <label className="block mb-4">
              <div className="eyebrow mb-2">Post content (paste it here)</div>
              <textarea required data-testid="swipe-form-content" className="textarea min-h-[220px]" value={form.content} onChange={e=>setForm({...form, content:e.target.value})} />
            </label>
            <label className="block mb-6">
              <div className="eyebrow mb-2">Tags</div>
              <input data-testid="swipe-form-tags" className="input" value={form.tags} onChange={e=>setForm({...form, tags:e.target.value})} placeholder="hook, contrarian, list" />
            </label>
            <div className="flex justify-end gap-3">
              <button type="button" className="btn btn-ghost" onClick={()=>setShowAdd(false)}>Cancel</button>
              <button disabled={busy} data-testid="swipe-form-submit" className="btn btn-accent">{busy?"Deconstructing…":"Save & analyze"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
